import { useMedplum } from '@medplum/react';
import { useEffect, useState } from 'react';
import {
  InsurerOption,
  loadInsurerOptions,
} from '@/components/Timeline/TimelineItems/PatientAccessCall/useInsurerOptions';

/** bariendo-be's every-state payer (`scripts/data/insurance-payers.ts`). Keep in sync. */
export const BCBS_OUT_OF_STATE_PAYER_KEY = 'bcbs-out-of-state';

/**
 * bariendo-be's Orbit-only payer (`ORBIT_ONLY_PAYERS`, BAR-3080): seeded with no state tag, so
 * signup never offers it. For an insurer that is on no list; its real name goes in the note.
 */
export const OTHER_PAYER_KEY = 'other';

const CATCH_ALL_KEYS: readonly string[] = [BCBS_OUT_OF_STATE_PAYER_KEY, OTHER_PAYER_KEY];

/** Alphabetical, then *Blue Cross from another state*, then *Other*. */
export function orderInsurerList(options: InsurerOption[]): InsurerOption[] {
  const rest = options
    .filter((option) => !CATCH_ALL_KEYS.includes(option.key))
    .sort((a, b) => a.name.localeCompare(b.name));
  const catchAlls = CATCH_ALL_KEYS.flatMap((key) => options.filter((option) => option.key === key));
  return [...rest, ...catchAlls];
}

/**
 * Every insurer on the state lists, merged, in `orderInsurerList`'s order (BAR-3079), with the
 * Orbit-only Other (BAR-3080). One list everywhere Orbit asks for an insurer outside the PN card,
 * whose list stays the patient's state's (BAR-2919). Read from the insurer Organizations
 * bariendo-be seeds, so a state list's change shows here too. Not read while `enabled` is false.
 */
export function useInsurerList({ enabled }: { enabled: boolean }): {
  options: InsurerOption[];
  loading: boolean;
  failed: boolean;
} {
  const medplum = useMedplum();
  const [options, setOptions] = useState<InsurerOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    loadInsurerOptions(medplum)
      .then((loaded) => !cancelled && setOptions(orderInsurerList(loaded)))
      .catch(() => !cancelled && setFailed(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [medplum, enabled]);

  return { options, loading, failed };
}
